import React, { useState, useEffect } from "react";
import { Combobox } from '@headlessui/react';
import { ChevronUpDownIcon, CheckIcon, MagnifyingGlassIcon, UserIcon } from '@heroicons/react/24/outline';
import storageService from "../../../services/storageService";

const RowOwnerField = (props) => {
  const { fieldName, fieldLabel, fieldMandatory, data, UsersPickListValues, GroupPickListValues } = props;
  const [selectedOption, setSelectedOption] = useState(null);
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  const processPicklistValues = (values, type) => {
    if (!values || typeof values !== 'object') return [];
    
    let processed = [];
    if (Array.isArray(values)) {
      processed = values.map((item, index) => ({
        value: item.value || item.key || index,
        label: item.label || item.value || item,
        type: type
      }));
    } else {
      processed = Object.entries(values).map(([key, value]) => ({
        value: key,
        label: value || key,
        type: type
      }));
    }
    return processed;
  };

  const userOptions = processPicklistValues(UsersPickListValues, 'User');
  const groupOptions = processPicklistValues(GroupPickListValues, 'Group');
  const allOptions = [...userOptions, ...groupOptions];

  useEffect(() => {
    const setDefaultUser = async () => {
      try {
        
        const user = await storageService.getUser();
        
        // Check if this is create mode (no existing data for this field)
        const hasExistingData = data && data.hasOwnProperty(fieldName) && data[fieldName];
        
        if (hasExistingData) {
          // Edit mode - use existing value
          let existingValue = data[fieldName];
          if (typeof existingValue === 'object' && existingValue.id) {
            existingValue = existingValue.id;
          }
          
          const existingOption = allOptions.find(option => option.value === existingValue);
          if (existingOption) {
            setSelectedOption(existingOption);
            setQuery(existingOption.label);
            return;
          }
        }
        
        // Create mode or no existing data - set current user as default
        if (user?.userid) {
          
          // Try to find user by userid
          let defaultOption = allOptions.find(option => 
            option.value === user.userid || 
            option.value === user.userid.toString()
          );
          
          // If not found by userid, try by username
          if (!defaultOption && user.username) {
            defaultOption = allOptions.find(option => 
              option.label === user.username ||
              option.label.includes(user.firstname) ||
              option.label.includes(user.lastname)
            );
          }
          
          // If still not found, try the first user option as fallback
          if (!defaultOption && userOptions.length > 0) {
            defaultOption = userOptions[0];
          }
          
          if (defaultOption) {
            setSelectedOption(defaultOption);
            setQuery(defaultOption.label);
          } else {
          }
        } else {
        }
      } catch (error) {
      }
    };

    // Only set default if we have options available
    if (allOptions.length > 0) {
      setDefaultUser();
    }
  }, [data, fieldName, allOptions.length]); // Added allOptions.length to dependencies

  const handleSelectionChange = (option) => {
    setSelectedOption(option);
    setIsOpen(false);
    if (option) {
      setQuery(option.label);
    } else {
      setQuery('');
    }
  };

  const filteredOptions = query === '' 
    ? allOptions 
    : allOptions.filter((option) =>
        option.label.toLowerCase().includes(query.toLowerCase())
      );

  const groupedOptions = filteredOptions.reduce((acc, option) => {
    if (!acc[option.type]) {
      acc[option.type] = [];
    }
    acc[option.type].push(option);
    return acc;
  }, {});

  return (
    <div className="mb-4">
      <label htmlFor={fieldName} className="block text-sm font-medium text-gray-700 mb-1 text-left">
        {fieldLabel}
        {fieldMandatory && <span className="text-red-500 ml-1">*</span>}
      </label>
      
      <input 
        type="hidden" 
        name={fieldName} 
        value={selectedOption?.value || ''} 
        required={fieldMandatory}
        className={fieldMandatory && (!selectedOption || !selectedOption.value) ? 'invalid' : ''}
      />
      
      <Combobox value={selectedOption} onChange={handleSelectionChange}>
        <div className="relative">
          <Combobox.Button 
            className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-colors text-left bg-white flex items-center justify-between pl-10 ${
              fieldMandatory && (!selectedOption || !selectedOption.value) 
                ? 'border-red-500 ring-red-500' 
                : 'border-gray-300'
            }`}
            onClick={() => setIsOpen(!isOpen)}
          >
            <span className={`block truncate ${selectedOption ? 'text-gray-900' : 'text-gray-500'}`}>
              {selectedOption ? selectedOption.label : `Select ${fieldLabel}...`}
            </span>
            <ChevronUpDownIcon className="h-4 w-4 text-gray-400 ml-2 flex-shrink-0" aria-hidden="true" />
          </Combobox.Button>
          <UserIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
          
          <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-hidden rounded-lg bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
            <div className="sticky top-0 bg-white border-b border-gray-200 p-2">
              <div className="relative">
                <Combobox.Input
                  className="w-full px-3 py-2 pl-10 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                  displayValue={(option) => option?.label || ''}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Search users and groups..."
                  onClick={(e) => e.stopPropagation()}
                />
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              </div>
            </div>
            
            <div className="max-h-48 overflow-y-auto py-1">
              <Combobox.Option
                className={({ active }) =>
                  `relative cursor-default select-none py-2 pl-10 pr-4 ${
                    active ? 'bg-gray-100 text-gray-900' : 'text-gray-500'
                  }`
                }
                value={null}
              >
                {({ selected, active }) => (
                  <>
                    <span className="block truncate font-normal italic">
                      Clear selection
                    </span>
                    {selected ? (
                      <span
                        className={`absolute inset-y-0 left-0 flex items-center pl-3 ${
                          active ? 'text-gray-900' : 'text-gray-500'
                        }`}
                      >
                        <CheckIcon className="h-4 w-4" aria-hidden="true" />
                      </span>
                    ) : null}
                  </>
                )}
              </Combobox.Option>
              
              {filteredOptions.length === 0 && query !== '' ? (
                <div className="relative cursor-default select-none py-2 px-4 text-gray-700">
                  No users or groups found for "{query}".
                </div>
              ) : (
                <>
                  {groupedOptions.User && groupedOptions.User.length > 0 && (
                    <>
                      <div className="px-4 py-2 text-xs font-semibold text-gray-500 bg-gray-50 border-b border-gray-200">
                        Users
                      </div>
                      {groupedOptions.User.map((option, index) => (
                        <Combobox.Option
                          key={`user-${option.value}-${index}`}
                          className={({ active }) =>
                            `relative cursor-default select-none py-2 pl-10 pr-4 ${
                              active ? 'bg-primary-500 text-white' : 'text-gray-900'
                            }`
                          }
                          value={option}
                        >
                          {({ selected, active }) => (
                            <>
                              <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                                {option.label}
                              </span>
                              {selected ? (
                                <span
                                  className={`absolute inset-y-0 left-0 flex items-center pl-3 ${
                                    active ? 'text-white' : 'text-primary-500'
                                  }`}
                                >
                                  <CheckIcon className="h-4 w-4" aria-hidden="true" />
                                </span>
                              ) : null}
                            </>
                          )}
                        </Combobox.Option>
                      ))}
                    </>
                  )}
                  
                  {groupedOptions.Group && groupedOptions.Group.length > 0 && (
                    <>
                      <div className="px-4 py-2 text-xs font-semibold text-gray-500 bg-gray-50 border-b border-gray-200">
                        Groups
                      </div>
                      {groupedOptions.Group.map((option, index) => (
                        <Combobox.Option
                          key={`group-${option.value}-${index}`}
                          className={({ active }) =>
                            `relative cursor-default select-none py-2 pl-10 pr-4 ${
                              active ? 'bg-primary-500 text-white' : 'text-gray-900'
                            }`
                          }
                          value={option}
                        >
                          {({ selected, active }) => (
                            <>
                              <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                                {option.label}
                              </span>
                              {selected ? (
                                <span
                                  className={`absolute inset-y-0 left-0 flex items-center pl-3 ${
                                    active ? 'text-white' : 'text-primary-500'
                                  }`}
                                >
                                  <CheckIcon className="h-4 w-4" aria-hidden="true" />
                                </span>
                              ) : null}
                            </>
                          )}
                        </Combobox.Option>
                      ))}
                    </>
                  )}
                </>
              )}
            </div>
          </Combobox.Options>
        </div>
      </Combobox>
      
      {fieldMandatory && (!selectedOption || !selectedOption.value) && (
        <div className="mt-1 text-xs text-red-600">
          {fieldLabel} is required.
        </div>
      )}
      
      <div className="invalid-feedback mt-1 text-xs text-red-600" style={{ display: 'none' }}>
        {fieldLabel} is required.
      </div>
    </div>
  );
};

export default RowOwnerField;